import React, { useState, useEffect } from 'react';
import { Button, Alert, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import SettingsIcon from '@mui/icons-material/Settings';
import { autoWebViewJs } from '@droid/android';


interface AddWidgetButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const AddWidgetButton: React.FC<AddWidgetButtonProps> = ({ 
  onSuccess, 
  onError 
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [deviceManufacturer, setDeviceManufacturer] = useState<string>('');
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);

  // 获取设备厂商信息
  useEffect(() => {
    try {
      const manufacturerScript = `
var manufacturer = com.fanfanlo.emergencycall.utils.WidgetUtils.getDeviceManufacturerName();
manufacturer;`;
      
      const { javaResultString } = autoWebViewJs.callScript(manufacturerScript);
      setDeviceManufacturer(javaResultString || '通用设备');
    } catch (error) {
      console.warn('获取设备厂商信息失败:', error);
      setDeviceManufacturer('通用设备');
    }
  }, []);

  const handleOpenSettings = async () => {
    try {
      console.log('VERSION_AWARE_WIDGET尝试打开权限设置');
      
      const settingsScript = `
var success = com.fanfanlo.emergencycall.utils.WidgetUtils.openPermissionSettings();
success;`;
      
      const { javaResultString } = autoWebViewJs.callScript(settingsScript);
      const success = javaResultString === "true";
      
      if (success) {
        console.log('VERSION_AWARE_WIDGET权限设置页面已打开');
      } else {
        console.warn('VERSION_AWARE_WIDGET无法打开权限设置页面');
      }
    } catch (error) {
      console.error('VERSION_AWARE_WIDGET打开权限设置失败:', error);
    }
  };

  const executeAddWidget = async () => {
    try {
      setMessage('正在添加桌面快捷方式...');
      setMessageType('info');
      
      const addScript = `
var success = com.fanfanlo.emergencycall.utils.WidgetUtils.requestAddMedicalWidget();
success;`;
      
      const { javaResultString: addResult } = autoWebViewJs.callScript(addScript);
      const addSuccess = addResult === "true";
      
      if (addSuccess) {
        setMessage('选择界面已打开，请选择默认样式或自定义设置');
        setMessageType('success');
        onSuccess?.();
      } else {
        throw new Error('启动选择界面失败');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '添加桌面快捷方式失败';
      setMessage(errorMsg);
      setMessageType('error');
      onError?.(errorMsg);
    }
  };

  const handleAddWidget = async () => {
    try {
      setIsAdding(true);
      setMessage('正在检查设备权限需求...');
      setMessageType('info');

      // 使用新接口检查设备权限信息
      const permissionScript = `
var permissionInfo = com.fanfanlo.emergencycall.utils.WidgetUtils.getDevicePermissionInfo();
permissionInfo;`;
      
      const { javaResultString: permissionResult } = autoWebViewJs.callScript(permissionScript);
      const permissionInfo = JSON.parse(permissionResult || '{}');
      
      if (permissionInfo.needsPermission) {
        // 需要权限的设备，显示弹窗
//         const stepsScript = `
// var steps = com.fanfanlo.emergencycall.utils.WidgetUtils.getManualAddSteps();
// steps;`;
        
//         const { javaResultString: stepsResult } = autoWebViewJs.callScript(stepsScript);
        
        setMessage(`您的手机需要打开设置页面，找到 "桌面快捷方式" 的权限，并进行授权`);
        setMessageType('info');
        setShowPermissionGuide(true);
      } else {
        // 不需要权限的设备，直接添加
        await executeAddWidget();
      }
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '检查设备权限失败';
      setMessage(errorMsg);
      setMessageType('error');
      onError?.(errorMsg);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      <Button
        variant="outlined"
        color="primary"
        size="large"
        fullWidth
        // startIcon={<AddToHomeScreenIcon />}
        onClick={handleAddWidget}
        disabled={isAdding}
        sx={{
          py: 1.5,
          borderColor: '#DC2626',
          color: '#DC2626',
          '&:hover': {
            borderColor: '#B91C1C',
            backgroundColor: 'rgba(220, 38, 38, 0.04)',
          },
          '&.Mui-disabled': {
            opacity: 0.6,
          },
        }}
      >
        {isAdding ? '正在添加...' : '🚑 添加医疗呼救小部件（大图标）'}
      </Button>
      
      {message && (
        <Alert 
          severity={messageType} 
          sx={{ mt: 1 }}
          onClose={() => setMessage('')}
        >
          <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
            {message}
          </Typography>
        </Alert>
      )}
      
      {/* 权限引导弹窗 */}
      <Dialog 
        open={showPermissionGuide}
        onClose={() => setShowPermissionGuide(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          需要权限设置
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            检测到 {deviceManufacturer}，需要手动开启桌面快捷方式权限才能添加小部件。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={async () => {
              setShowPermissionGuide(false);
              // 关闭弹窗后执行添加功能
              await executeAddWidget();
            }}
            color="inherit"
          >
            已设置
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={handleOpenSettings}
            sx={{
              backgroundColor: '#DC2626',
              '&:hover': {
                backgroundColor: '#B91C1C',
              },
            }}
          >
            去设置
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* {!message && !showPermissionGuide && deviceManufacturer && (
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', mt: 1, textAlign: 'center' }}
        >
          当前设备：{deviceManufacturer} • 可选择默认样式或自定义设置
        </Typography>
      )} */}
    </Box>
  );
};

export default AddWidgetButton;